import { useEffect, useRef, useState } from "react";
import { NavbarInfo } from "../../services/interfaces/Inavbar";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
	AccountInfo,
	InteractionRequiredAuthError,
	InteractionStatus,
} from "@azure/msal-browser";
import { callMsGraph } from "../../services/msalAuth/MSALGraph";
import { loginRequest } from "../../services/msalAuth/authConfig";
import { useAppDispatch } from "../../redux/hooks/hooks";
import { useHomeData, useUserDetails } from "../../react-query/hooks/HHomePage";
import { updateUserDetails } from "../../redux/reducer/user.reducer";
import { ModuleData } from "../../services/interfaces/IHomePage";
import { Module_Name } from "../../utils/constants";
import {
	useNotificationsCount,
	useNotificationsData,
} from "../../react-query/hooks/HNotifications";
import { appInsights } from "../../router/router";
import UserGuideDownloadIcon from "../../assets/user-guide-download.svg";
import GreenHome from "../../assets/green-home.svg";
import { Divider } from "rsuite";
import Menu from "./Menu";
import useWdEdgeConsolidatedSource from "../../hooks/useWdEdgeConsolidatedSource";
import ConsolidatedHeader from "../Layouts/ConsolidatedHeader";

const navbar: NavbarInfo[] = [
	{
		name: "Negotiations & Allocations",
		tag: 1,
		fullName: "Negotiations & Allocations",
		route: null,
		internalRoute: [
			{ name: "Vendor Quote Assessment", route: "/quote-assessment" },
			{ name: "Volume Allocation", route: "/allocation" },
			{ name: "Allocation Summary", route: "/allocation-summary" },
		],
	},
	{
		name: "Business Cost",
		tag: 2,
		fullName: "Business Cost Setting",
		route: null,
		internalRoute: [
			{ name: "Business Cost Setting", route: "/cost-setting" },
			{ name: "Business Cost Trend", route: "/cost-trend" },
		],
	},
	{
		name: "Compliance Monitoring",
		tag: 3,
		fullName: "Compliance Monitoring",
		route: null,
		internalRoute: [
			{ name: "Cost Compliance", route: "/cost-compliance" },
			{ name: "Volume Compliance", route: "/volume-compliance" },
		],
	},
	{
		name: "NIA Opportunity",
		fullName: "NIA Opportunity",
		tag: 4,
		route: "/nia-opportunity",
		internalRoute: null,
	},
];

const navBarDropDowns: NavbarMenu[] = [
	{
		title: "Profitability",
		subMenus: [{
			name: "Profitability Summary",
			route: "/customer-profitability"
		},
		{
			name: "Profitability Waterfall",
			route: "/customer-profitability?active=breakDown"
		}]
	},
	{
		title: "Pricing",
		subMenus: [{
			name: "Pricing",
			route: "/pricing",
		}]
	},
	{
		title: "Cost to Serve",
		subMenus: [
			{
				name: "Cost to Serve",
				route: "/cts",
			},
		]
	},
	{
		title: "Strategic Growth",
		subMenus: [{
			name: "White Label Pricing",
			route: "/wlp",
		},
		{
			name: "Cross Sell",
			route: "/cross-sell",
		}]
	},
]


const wdConsolidatedNavBarDropDowns: NavbarMenu[] = [
	{
		title: "Pricing",
		subMenus: [{
			name: "Pricing",
			route: "/pricing",
		}]
	},
	{
		title: "Cost to Serve",
		subMenus: [
			{
				name: "Cost to Serve",
				route: "/cts",
			},
		]
	},
	{
		title: "Strategic Growth",
		subMenus: [{
			name: "White Label Pricing",
			route: "/wlp",
		},
		{
			name: "Cross Sell",
			route: "/cross-sell",
		}]
	},
]

const mapRouteToPage = (route: string) => {
	switch (route) {
		case "/home":
			return "Home";
		case "/quote-assessment":
			return "Negotiations & Allocations : Vendor Quote Assessment";
		case "/allocation":
			return "Negotiations & Allocations : Volume Allocation";
		case "/allocation-summary":
			return "Negotiations & Allocations : Allocation Summary";
		case "/cost-setting":
			return "Business Cost : Business Cost Setting";
		case "/cost-trend":
			return "Business Cost : Business Cost Trend";
		case "/cost-compliance":
			return "Compliance Monitoring : Cost Compliance";
		case "/volume-compliance":
			return "Compliance Monitoring : Volume Compliance";
		case "/nia-opportunity":
			return "NIA Opportunity";
		case "/notifications":
			return "Notifications";
		case "/reports":
			return "Reports";
	}
};

const Navbar = () => {
	const { isSourceWdEdgeConsolidated } = useWdEdgeConsolidatedSource();

	const [navbarInfo, setNavbarInfo] = useState<NavbarInfo[] | null>(navbar);
	const { instance, inProgress } = useMsal();
	const [graphData, setGraphData] = useState<any>(null);
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [userEmail, setUserEmail] = useState<string>("");
	const { userDetails, isLoading } = useUserDetails(userEmail);
	const { data: homeData, isLoading: modulesLoading } = useHomeData(
		null,
		null
	);

	useEffect(() => {
		dispatch(updateUserDetails(userDetails));
	}, [userDetails]);

	const firstRender = useRef<boolean>(true);

	useEffect(() => {
		if (!graphData && inProgress === InteractionStatus.None) {
			callMsGraph()
				.then((response) => {
					window.localStorage.setItem("userEmail", response.mail);
					setUserEmail(response.mail);
					setGraphData(response);

					let authHandler = localStorage.getItem("authHandler");
					if (authHandler && authHandler === "1") {
						localStorage.setItem("authHandler", "0");
					}
					localStorage.setItem("name", response?.displayName);

					if (firstRender.current && response !== null) {
						appInsights.setAuthenticatedUserContext(response.mail);
						appInsights.trackEvent({
							name: "User Details",
							properties: {
								email: response.mail,
								timeStamp: new Date().toUTCString(),
								region: Intl.DateTimeFormat().resolvedOptions()
									.timeZone,
							},
						});
						appInsights.startTrackPage("Commodity Connect");
						firstRender.current = false;
					}
				})
				.catch((e) => {
					if (e instanceof InteractionRequiredAuthError) {
						instance.acquireTokenRedirect({
							...loginRequest,
							account: instance.getActiveAccount() as AccountInfo,
						});
					}
				});
		}
	}, [inProgress, graphData, instance]);

	useEffect(() => {
		if (homeData && Object.keys(homeData).length > 0) {
			let navBarCopy =
				navbarInfo &&
				navbarInfo.map((nav: NavbarInfo) => {
					let i = homeData.data.findIndex(
						(module: ModuleData) =>
							Module_Name[
							module.name as keyof typeof Module_Name
							] === nav.fullName
					);
					if (i >= 0) {
						nav.days_left_for_review =
							homeData.data[i].days_left_for_review;
					}
					return nav;
				});
			if (navBarCopy && navBarCopy.length > 0) {
				setNavbarInfo([...navBarCopy]);
			}
		}
	}, [homeData]);

	useEffect(() => {
		if (location.pathname === "/") return;
		let startTime = Date.now();

		appInsights.trackPageView({
			name: `${mapRouteToPage(location.pathname)}`,
		});
		handleOpen("")
		let currentNavButton = "";
		let currentNavText = "";
		navBarDropDowns.forEach((n: NavbarMenu) => {
			if (location.pathname == "/home") {
				currentNavButton = "Home";
				currentNavText = "Home";
			}
			else {
				const subMenu = n.subMenus.find((s) => s.route === location.pathname);
				if (subMenu) {
					currentNavButton = n.title;
					currentNavText = subMenu.name;
				}
			}
		});
		setSelectedNavButton(currentNavButton);
		return () => {
			if (location.pathname === "/") return;
			const duration = Date.now() - startTime;
			appInsights.trackEvent({
				name: `Duration for ${mapRouteToPage(location.pathname)} - ${duration / 1000
					}s`,
			});
		};
	}, [location]);
	const [selectedNavButton, setSelectedNavButton] = useState("home");
	const [openedMenu, setOpenedMenu] = useState("");

	const handleOpen = (openedTitle: string) => {
		if (openedTitle === openedMenu)
			setOpenedMenu("")
		else
			setOpenedMenu(openedTitle)
	}

	return (
		<div className="h-full border-r-[1px] border-[#BDBDBD] flex flex-col justify-between">
			<div className="header-container flex flex-col justify-between w-full border-[#949494] bg-white">
				<div className="flex items-center gap-1 text-[1.2vw] text-black px-[1.5%] py-[0.2vw]">
					{isSourceWdEdgeConsolidated ?

						<span className="font-[700] text-[#03234d] [font-size:clamp(.8rem,-.2909090909rem_+_1.4914772727vw,3.8rem)]">Margin and Service Improvement Control Tower</span> :
						<span className="font-[700]">
							Margin
							<span className="font-[400] ">
								Brain
								<sup className="text-[0.8vw] font-[600]">TM</sup>
							</span>
						</span>}
					<span>
						<Divider
							className="line h-[3.8vh] w-[1.2px]"
							vertical
						/>
					</span>
					<div className="flex flex-col">
						<div className="text-[1vw]  font-[800] leading-none">
							<span className="text-deloitteGreen">WD</span>
							<span className="text-black">
								Edge{" "}
								<sup className="text-[0.4vw] font-[600] top-[-1vh] right-[0.5vh]">
									TM
								</sup>
							</span>
						</div>
						<div className="text-[0.7vw] leading-none">
							<span className="leading-none">By</span>
							<span className="font-extrabold leading-none">
								Deloitte
							</span>
							<span className="font-bold text-deloitteGreen">.</span>
						</div>
					</div>
				</div>
				<div className="flex w-full px-[1.5%] gap-1 mt-1 bg-lightWhite shadow-md">
					{isSourceWdEdgeConsolidated ? <ConsolidatedHeader />
						:
						<>
							<div onClick={() => {
								navigate("/home")
							}} className={`flex justify-start items-center gap-1 !cursor-pointer px-[1vw] py-[1.6vh] relative ${selectedNavButton == "Home"
								? "border-b-[3px] border-deloitteGreen font-semibold text-deloitteGreen"
								: ""
								} `}>
								<div className='flex w-full  cursor-pointer'>
									<img className="w-[0.6vw]" src={GreenHome} alt="Home" />
									<p className={`navbar-menu font-semibold mx-[0.7vw] text-black `}>Home</p>
								</div>
							</div>
							{navBarDropDowns.map((dropDownMenu) => (
								<div key={dropDownMenu.title} className={`${dropDownMenu.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`} onClick={() => {
									if (!dropDownMenu.disabled)
										handleOpen(dropDownMenu.title)
								}}>
									<Menu menu={dropDownMenu} isSelected={selectedNavButton === dropDownMenu.title} isOpen={openedMenu === dropDownMenu.title} handleOpen={handleOpen} />
								</div>
							))}
						</>}
				</div>
			</div>
		</div>
	);
};

export default Navbar;
